/**
 * Money — importe en la moneda base (USD, unidades menores) con su equivalente
 * en VES: `$25.00 / Bs. 912,50 @ 36,50`.
 *
 * **El cliente NO convierte.** El bolívar llega ya convertido del servidor
 * (campos `*VesMinor`), que es quien tiene la tasa, la política de redondeo y la
 * responsabilidad contable. Antes este componente multiplicaba por la tasa en
 * unidades MAYORES y dejaba redondear a `Intl`, mientras el servidor redondeaba
 * a unidad menor: dos redondeos para el mismo importe, y el mismo número podía
 * imprimirse distinto según la pantalla. Peor todavía, los campos
 * `totalDebtVesMinor` / `savingsVesMinor` / `totalVesMinor` que el servidor YA
 * calculaba no los consumía nadie.
 *
 * La parte VES sólo aparece si el servidor la mandó: nunca se inventa una
 * conversión. Si un importe necesita bolívares y el servidor no los ofrece, el
 * campo se añade en el SERVIDOR — no se reintroduce la conversión aquí.
 */
import { type Component, Show } from "solid-js";

interface MoneyProps {
  /** Importe en USD, unidades menores (céntimos). */
  usdMinor: number;
  /**
   * El MISMO importe en VES, unidades menores, tal como lo calculó el servidor.
   * Ausente/`null` = sin tasa capturada: se muestra sólo el USD.
   */
  vesMinor?: number | null;
  /** Tasa VES por USD vigente — sólo para la etiqueta `@ 36,50`. */
  vesPerUsd?: number | null;
  /** Mostrar la línea de tasa `@ 36,50`. Por defecto true si hay tasa. */
  showRate?: boolean;
  locale?: string;
  class?: string;
}

const MINOR = 100;

const format = (minor: number, currency: string, locale: string): string => {
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(minor / MINOR);
  } catch {
    // Moneda no reconocida por Intl: fallback legible.
    return `${(minor / MINOR).toFixed(2)} ${currency}`;
  }
};

export const Money: Component<MoneyProps> = (props) => {
  const locale = () => props.locale ?? "es-VE";
  const ves = () => {
    const v = props.vesMinor;
    return v === null || v === undefined || !Number.isFinite(v) ? null : v;
  };

  const rateLabel = () => {
    const r = props.vesPerUsd;
    if (r === null || r === undefined || !Number.isFinite(r) || r <= 0) {
      return null;
    }
    return new Intl.NumberFormat(locale(), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(r);
  };

  return (
    <span class={props.class} data-testid="money">
      <span class="font-semibold tabular-nums">{format(props.usdMinor, "USD", locale())}</span>
      <Show when={ves() !== null}>
        <span class="text-muted-foreground"> / </span>
        <span class="tabular-nums text-muted-foreground" data-testid="money-ves">
          {format(ves() ?? 0, "VES", locale())}
        </span>
      </Show>
      <Show when={(props.showRate ?? true) && ves() !== null && rateLabel()}>
        <span class="text-xs text-muted-foreground"> @ {rateLabel()}</span>
      </Show>
    </span>
  );
};
